import jsPDF from "jspdf";

export const exportAttendancePDF = (
  labourName, labourEmail, dailyRate, records = [], month = "", payments = []
) => {
  try {
    const doc = new jsPDF("p","mm","a4");
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();
    const M = 15, CW = W - M * 2;

    // ── Palette ──────────────────────────────────────────────────
    const C = {
      amber:   [180, 83,  9],  amberD:  [120, 53, 15],  amberL:  [254,243,199],
      amberBg: [255,251,243],  amberBdr:[253,186, 94],
      green:   [  4,120, 87],  greenBg: [236,253,245],  greenBdr:[110,231,183],
      red:     [185, 28, 28],  redBg:   [255,241,241],  redBdr:  [252,165,165],
      blue:    [  3,105,161],  blueBg:  [239,246,255],  blueBdr: [147,197,253],
      orange:  [180, 83,  9],  orangeBg:[255,247,237],  orangeBdr:[253,186,94],
      purple:  [109, 40,217],  purpleBg:[245,243,255],  purpleBdr:[167,139,250],
      dark:    [ 15, 10,  5],  mid:     [ 78, 73, 68],  muted:   [160,155,150],
      border:  [225,220,215],  softBg:  [250,248,246],  white:   [255,255,255],
      rowAlt:  [252,250,248],
    };

    const F  = (...c)    => doc.setFillColor(...c);
    const D  = (...c)    => doc.setDrawColor(...c);
    const TC = (...c)    => doc.setTextColor(...c);
    const LW = n         => doc.setLineWidth(n);
    const fnt= (sz,s)    => { doc.setFontSize(sz); doc.setFont("helvetica",s||"normal"); };
    const tx = (s,x,y,o) => doc.text(String(s),x,y,o||{});
    const pad= n         => String(n).padStart(2,"0");

    const fillR= (x,y,w,h,...fc) => { F(...fc); doc.rect(x,y,w,h,"F"); };
    const rndBox= (x,y,w,h,r,fc,dc,lw=0.4) => {
      F(...fc); D(...dc); LW(lw);
      doc.roundedRect(x,y,w,h,r,r,"FD");
    };

    // ── Status ───────────────────────────────────────────────────
    const ST = {
      full:     { label:"Full Day",  bg:C.greenBg,  bdr:C.greenBdr,  tc:C.green  },
      half:     { label:"Half Day",  bg:C.orangeBg, bdr:C.orangeBdr, tc:C.orange },
      overtime: { label:"Overtime",  bg:C.blueBg,   bdr:C.blueBdr,   tc:C.blue   },
      absent:   { label:"Absent",    bg:C.redBg,    bdr:C.redBdr,    tc:C.red    },
      holiday:  { label:"Holiday",   bg:C.orangeBg, bdr:C.orangeBdr, tc:C.orange },
      leave:    { label:"On Leave",  bg:C.purpleBg, bdr:C.purpleBdr, tc:C.purple },
    };

    // ── Payment map ──────────────────────────────────────────────
    const payMap = {};
    payments.forEach(p => {
      const d   = new Date(p.date||p.createdAt);
      const key = `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
      if (!payMap[key]) payMap[key] = {ot:0,adv:0,pay:0};
      if (p.type==="overtime")                    payMap[key].ot  += p.amount;
      if (p.type==="advance")                     payMap[key].adv += p.amount;
      if (p.type==="payment")                     payMap[key].pay += p.amount;
    });

    // ── Data ─────────────────────────────────────────────────────
    const name   = (labourName ||"Unknown").trim();
    const email  = (labourEmail||"").trim();
    const ini    = name.split(" ").filter(Boolean).map(w=>w[0]).join("").slice(0,2).toUpperCase()||"?";
    const WORKED = ["full","half","overtime"];
    const fil    = month ? records.filter(r=>r.date?.startsWith(month)) : records;
    const sorted = [...fil].sort((a,b)=>new Date(b.date)-new Date(a.date));
    const total  = fil.length;
    const fullD  = fil.filter(r=>r.status==="full").length;
    const halfD  = fil.filter(r=>r.status==="half").length;
    const otD    = fil.filter(r=>r.status==="overtime").length;
    const absD   = fil.filter(r=>r.status==="absent").length;
    const holD   = fil.filter(r=>r.status==="holiday").length;
    const lvD    = fil.filter(r=>r.status==="leave").length;
    const wrkD   = fil.filter(r=>WORKED.includes(r.status)).length;
    const pct    = total ? Math.round((wrkD/total)*100) : 0;
    const pctC   = pct>=75?C.green:pct>=50?C.amber:C.red;
    const totalOT  = payments.filter(p=>p.type==="overtime").reduce((s,p)=>s+p.amount,0);
    const totalAdv = payments.filter(p=>p.type==="advance"||p.type==="payment").reduce((s,p)=>s+p.amount,0);
    const monthLabel= month
      ? new Date(month+"-01").toLocaleDateString("en-IN",{month:"long",year:"numeric"})
      : "All Time";
    const todayStr = new Date().toLocaleDateString("en-IN",{day:"numeric",month:"long",year:"numeric"});

    // ═══════════════════════════════════════════════════════════════
    // PAGE SHELL
    // ═══════════════════════════════════════════════════════════════
    const drawShell = (isFirst) => {
      fillR(0,0,W,H,...C.white);

      if (isFirst) {
        // Header card — solid amber dark strip left, warm bg right
        fillR(0,0,W,52,...C.amberBg);
        fillR(0,0,10,52,...C.amberD);    // left accent strip
        fillR(10,0,1,52,...C.amber);     // thin separator

        // Top-right corner accent
        fillR(W-40,0,40,52,...C.amberL);
        D(...C.amberBdr); LW(0.3); doc.line(W-40,0,W-40,52);

        // Brand
        fnt(15,"bold"); TC(...C.amberD); tx("Craftigo", 16, 18);
        fnt(7,"normal"); TC(...C.amber); tx("Labour Management System", 16, 25);

        // Divider in header
        D(...C.amberBdr); LW(0.3); doc.line(16,29,W-44,29);

        // Period + date below divider
        fnt(7,"bold"); TC(...C.amberD); tx("Period:", 16, 36);
        fnt(7,"normal"); TC(...C.mid); tx(monthLabel, 31, 36);
        fnt(7,"bold"); TC(...C.amberD); tx("Generated:", 16, 43);
        fnt(7,"normal"); TC(...C.mid); tx(todayStr, 38, 43);

        // Right corner — report label
        fnt(8,"bold"); TC(...C.amberD); tx("ATTENDANCE", W-20, 22, {align:"center"});
        fnt(8,"bold"); TC(...C.amberD); tx("REPORT",     W-20, 30, {align:"center"});

        // Bottom border of header
        D(...C.amberBdr); LW(0.5); doc.line(0,52,W,52);

      } else {
        fillR(0,0,W,13,...C.amberBg);
        fillR(0,0,4,13,...C.amberD);
        D(...C.amberBdr); LW(0.3); doc.line(0,13,W,13);
        fnt(7,"bold"); TC(...C.amberD); tx("Craftigo", 9, 9);
        fnt(7,"normal"); TC(...C.mid);
        tx(name+"  ·  Attendance Report  ·  "+monthLabel, 25, 9);
      }
    };

    const drawFooter = () => {
      fillR(0,H-11,W,11,...C.softBg);
      D(...C.border); LW(0.3); doc.line(0,H-11,W,H-11);
      fnt(6,"normal"); TC(...C.muted);
      tx("Craftigo  ·  "+name, M, H-4.5);
      fnt(6,"normal"); TC(...C.muted);
      tx(
        "Full:"+fullD+"  Half:"+halfD+"  OT:"+otD+"  Absent:"+absD+
        "  Holiday:"+holD+"  Leave:"+lvD+
        "   |   OT Pay: Rs."+totalOT+"   Total Paid: Rs."+totalAdv,
        W-M, H-4.5, {align:"right"}
      );
    };

    // ═══════════════════════════════════════════════════════════════
    // PAGE 1
    // ═══════════════════════════════════════════════════════════════
    drawShell(true);
    let y = 58;

    // ── Profile card ─────────────────────────────────────────────
    rndBox(M,y,CW,28,3,C.white,C.border,0.5);

    // Avatar
    F(...C.amberL); D(...C.amberBdr); LW(0.5);
    doc.circle(M+16,y+14,11,"FD");
    fnt(9,"bold"); TC(...C.amberD); tx(ini,M+16,y+17.5,{align:"center"});

    // Name & email
    fnt(12,"bold"); TC(...C.dark); tx(name, M+32, y+11);
    fnt(7.5,"normal"); TC(...C.mid); if(email) tx(email, M+32, y+18);

    // Chips
    let cx = M+32, cy = y+(email?22:18);
    if(dailyRate!=null){
      rndBox(cx,cy,28,5,1.5,C.greenBg,C.greenBdr,0.4);
      fnt(6,"bold"); TC(...C.green); tx("Rs."+dailyRate+"/day",cx+14,cy+3.5,{align:"center"});
      cx+=31;
    }
    rndBox(cx,cy,28,5,1.5,C.amberBg,C.amberBdr,0.4);
    fnt(6,"bold"); TC(...C.amberD); tx(monthLabel,cx+14,cy+3.5,{align:"center"});

    // Attendance % — right of card
    const pctStr = pct+"%";
    fnt(20,"bold"); TC(...pctC); tx(pctStr, W-M-8, y+15, {align:"right"});
    fnt(6,"normal"); TC(...C.muted); tx("attendance", W-M-8, y+21, {align:"right"});

    // Progress bar under % text
    const pbx=W-M-38, pby=y+23, pbw=36, pbh=3;
    F(...C.border); doc.roundedRect(pbx,pby,pbw,pbh,1.5,1.5,"F");
    if(pct>0){ F(...pctC); doc.roundedRect(pbx,pby,pbw*(pct/100),pbh,1.5,1.5,"F"); }

    y += 34;

    // ── 6 stat boxes ─────────────────────────────────────────────
    const stats = [
      {label:"Full Day",  val:fullD,          ...{bg:C.greenBg, bdr:C.greenBdr, tc:C.green}  },
      {label:"Half Day",  val:halfD,          ...{bg:C.orangeBg,bdr:C.orangeBdr,tc:C.orange} },
      {label:"Overtime",  val:otD,            ...{bg:C.blueBg,  bdr:C.blueBdr,  tc:C.blue}   },
      {label:"Absent",    val:absD,           ...{bg:C.redBg,   bdr:C.redBdr,   tc:C.red}    },
      {label:"OT Pay",    val:"Rs."+totalOT,  ...{bg:C.blueBg,  bdr:C.blueBdr,  tc:C.blue}   },
      {label:"Paid",      val:"Rs."+totalAdv, ...{bg:C.redBg,   bdr:C.redBdr,   tc:C.red}    },
    ];
    const sw = (CW-10)/6;
    stats.forEach((s,i) => {
      const sx = M+i*(sw+2);
      rndBox(sx,y,sw,17,2.5,s.bg,s.bdr,0.4);
      // Top accent line
      F(...s.tc); doc.roundedRect(sx,y,sw,2,2.5,2.5,"F"); doc.rect(sx,y+1,sw,1,"F");
      const vStr=String(s.val);
      fnt(vStr.length>5?8:11,"bold"); TC(...s.tc);
      tx(vStr,sx+sw/2,y+10.5,{align:"center"});
      fnt(5.5,"normal"); TC(...C.mid);
      tx(s.label,sx+sw/2,y+15.5,{align:"center"});
    });
    y += 23;

    // ── Worked summary line ───────────────────────────────────────
    fillR(M,y,CW,9,...C.softBg);
    D(...C.border); LW(0.3); doc.rect(M,y,CW,9,"S");
    fnt(6.5,"normal"); TC(...C.mid);
    tx("Worked "+wrkD+" of "+total+" days  (Full + Half + Overtime)", M+6, y+5.8);
    // small bar
    const sbx=M+CW-52, sby=y+2.5, sbw=46, sbh=4;
    F(...C.border); doc.roundedRect(sbx,sby,sbw,sbh,2,2,"F");
    if(pct>0){ F(...pctC); doc.roundedRect(sbx,sby,sbw*(pct/100),sbh,2,2,"F"); }
    fnt(6.5,"bold"); TC(...pctC); tx(pct+"%", sbx+sbw+3, y+5.8);
    y += 15;

    // ── Table ─────────────────────────────────────────────────────
    // Column x positions
    const CO = { num:M+4, date:M+13, day:M+53, st:M+95, pay:W-M-2 };

    // Table header
    fillR(M,y,CW,8,...C.amberD);
    // subtle right accent
    fillR(M+CW-30,y,30,8,...C.amber);
    fnt(6.5,"bold"); TC(...C.white);
    tx("#",       CO.num,  y+5.5);
    tx("DATE",    CO.date, y+5.5);
    tx("DAY",     CO.day,  y+5.5);
    tx("STATUS",  CO.st,   y+5.5);
    tx("PAYMENT", CO.pay,  y+5.5, {align:"right"});
    y += 8;

    const rowH = 8;
    const maxR = Math.floor((H-y-14)/rowH);
    let lr = 0;

    sorted.forEach((r,i) => {
      // New page
      if(lr>0 && lr%maxR===0){
        drawFooter(); doc.addPage(); drawShell(false);
        y=19;
        fillR(M,y,CW,8,...C.amberD);
        fillR(M+CW-30,y,30,8,...C.amber);
        fnt(6.5,"bold"); TC(...C.white);
        tx("#",CO.num,y+5.5); tx("DATE",CO.date,y+5.5);
        tx("DAY",CO.day,y+5.5); tx("STATUS",CO.st,y+5.5);
        tx("PAYMENT",CO.pay,y+5.5,{align:"right"});
        y+=8; lr=0;
      }

      const ry  = y+lr*rowH;
      const d   = new Date(r.date);
      const key = `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
      const st  = ST[r.status]||{label:r.status,bg:C.softBg,bdr:C.border,tc:C.mid};
      const pay = payMap[key]||{ot:0,adv:0};

      // Row bg alternating
      if(i%2===0) fillR(M,ry,CW,rowH,...C.white);
      else        fillR(M,ry,CW,rowH,...C.rowAlt);

      // Left status color strip (thin, elegant)
      const sc = st.tc;
      F(...sc); doc.rect(M,ry,2.5,rowH,"F");

      // Row bottom border
      D(...C.border); LW(0.1); doc.line(M,ry+rowH,M+CW,ry+rowH);

      // #
      fnt(6,"normal"); TC(...C.muted); tx(String(i+1), CO.num, ry+5.2);

      // Date
      fnt(6.5,"bold"); TC(...C.dark);
      tx(d.toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"}), CO.date, ry+5.2);

      // Day
      fnt(6.5,"normal"); TC(...C.mid);
      tx(d.toLocaleDateString("en-IN",{weekday:"long"}), CO.day, ry+5.2);

      // Status badge
      const stLabel = r.status==="overtime"&&r.overtimeHours
        ? st.label+" "+r.overtimeHours+"h" : st.label;
      const sbw = stLabel.length*2.05+6;
      rndBox(CO.st, ry+1.3, sbw, 5.5, 1.5, st.bg, st.bdr, 0.3);
      fnt(5.5,"bold"); TC(...st.tc); tx(stLabel, CO.st+sbw/2, ry+5.2, {align:"center"});

      // Payment badges — labeled
      let px = CO.pay;
      if(pay.adv>0){
        const s="ADVANCE  -Rs."+pay.adv;
        const bw=s.length*1.85+7;
        rndBox(px-bw, ry+1.3, bw, 5.5, 1.5, C.redBg, C.redBdr, 0.3);
        fnt(5.5,"bold"); TC(...C.red); tx(s, px-bw/2, ry+5.2, {align:"center"});
        px = px-bw-3;
      }
      if(pay.pay>0){
        const s="PAYMENT  Rs."+pay.pay;
        const bw=s.length*1.85+7;
        rndBox(px-bw, ry+1.3, bw, 5.5, 1.5, C.greenBg, C.greenBdr, 0.3);
        fnt(5.5,"bold"); TC(...C.green); tx(s, px-bw/2, ry+5.2, {align:"center"});
        px = px-bw-3;
      }
      if(pay.ot>0){
        const s="OVERTIME  +Rs."+pay.ot;
        const bw=s.length*1.85+7;
        rndBox(px-bw, ry+1.3, bw, 5.5, 1.5, C.blueBg, C.blueBdr, 0.3);
        fnt(5.5,"bold"); TC(...C.blue); tx(s, px-bw/2, ry+5.2, {align:"center"});
      }
      if(!pay.ot&&!pay.adv&&!pay.pay){
        fnt(6,"normal"); TC(...C.border); tx("—", CO.pay, ry+5.2, {align:"right"});
      }

      lr++;
    });

    if(sorted.length===0){
      fillR(M,y,CW,20,...C.softBg);
      fnt(9,"normal"); TC(...C.muted);
      tx("No records found.", W/2, y+12, {align:"center"});
    }

    drawFooter();
    const safe=name.replace(/\s+/g,"_").toLowerCase();
    doc.save(safe+"_attendance"+(month?"_"+month:"_all")+".pdf");

  } catch(e){ console.error("PDF Export Error:",e); }
};